// Content moderation utilities for leaderboard names

// Basic profanity filter - you can expand this list as needed
const profanityList = [
  // Common profanity (asterisks to avoid triggering filters)
  'f*ck', 'sh*t', 'b*tch', 'a**hole', 'damn', 'hell',
  'd*mn', 'cr*p', 'p*ss', 'b*st*rd', 'w*ore', 'sl*t',
  'tw*t', 'c*ck', 'p*nis', 'v*gina', 'b*lls', 'n*ts',
  'pr*ck', 'w*nker', 't*ts', 'a*s', 'f*rt', 'p*op',
  // Variations with numbers/symbols
  'f4ck', 'sh1t', 'b1tch', 'a55hole', 'fu*k', 'sh!t',
  'tw4t', 'c0ck', 'pen1s', 'vag1na', 'b4lls', 'n7ts',
  'pr1ck', 'w4nker', 't1ts', '4ss', 'f4rt', 'p00p',
  // Add more as needed - this is just a starting set
];

// Convert to actual characters for matching
const actualProfanityList = profanityList.map(word => 
  word.replace(/\*/g, '').replace(/4/g, 'a').replace(/1/g, 'i').replace(/!/g, 'i').replace(/5/g, 's')
);

// Inappropriate patterns to check for
const inappropriatePatterns = [
  /n[i1!]gg[aer]/i,  // Racial slurs
  /f[a@4]g[gs]?/i,   // Homophobic slurs
  /r[e3]t[a@4]rd/i,  // Ableist slurs
  /k[y1!]ke/i,       // Anti-Semitic slurs
  /sp[i1!]ck?/i,     // Ethnic slurs
  /ch[i1!]nk/i,      // Ethnic slurs
  /g[o0]ok/i,        // Ethnic slurs
  /wh[o0]re/i,       // Derogatory terms
  /sl[u*]t/i,       // Derogatory terms
  /b[i1!]tch/i,      // Profanity variations
  /f[u*]ck/i,       // Profanity variations
  /sh[i1!]t/i,       // Profanity variations
  /d[i1!]ck/i,       // Profanity variations
  /c[u*]nt/i,       // Strong profanity
  /a[s5$]sh[o0]le/i, // Profanity variations
  /tw[a@4]t/i,       // British profanity
  /c[o0]ck/i,        // Anatomical profanity
  /p[e3]n[i1!]s/i,   // Anatomical terms
  /v[a@4]g[i1!]n[a@4]/i, // Anatomical terms
  /b[a@4]lls?/i,     // Anatomical slang
  /n[u*]ts/i,        // Anatomical slang
  /pr[i1!]ck/i,      // British profanity
  /w[a@4]nk[e3]r/i,  // British profanity
  /t[i1!]ts/i,       // Anatomical slang
  /[a@4]ss/i,        // Common profanity
  /p[o0]{2}p/i,      // Scatological terms
  /[0o]rg[a@4]sm/i,  // Sexual content
  /m[a@4]sturb[a@4]t/i, // Sexual content
  /p[o0]rn/i,        // Sexual content
  /xxx/i,            // Sexual content indicator
];

// Spam patterns (repeated characters, excessive caps, etc.)
const spamPatterns = [
  /(.)\1{4,}/,       // More than 4 repeated characters
  /^[A-Z]{8,}$/,     // All caps with 8+ characters
  /[0-9]{6,}/,       // Long number sequences
  /^[^a-zA-Z0-9\s]+$/, // Only special characters
];

// Reserved/inappropriate usernames
const reservedNames = [
  'admin', 'administrator', 'mod', 'moderator', 'owner',
  'system', 'official', 'staff', 'support', 'help',
  'smilesflow', 'test', 'null', 'undefined',
  'anonymous', 'guest', 'user', 'player', 'winner', 'champion',
  'god', 'jesus', 'allah', 'buddha', 'satan', 'devil',
  'hitler', 'nazi', 'isis', 'terrorist'
];

// Helper function to normalize text for comparison
const normalizeText = (text) => {
  return text.toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric
    .replace(/0/g, 'o')
    .replace(/1/g, 'i')
    .replace(/3/g, 'e')
    .replace(/4/g, 'a')
    .replace(/5/g, 's')
    .replace(/7/g, 't')
    .replace(/8/g, 'b');
};

// Check if text contains profanity
const containsProfanity = (text) => {
  const normalized = normalizeText(text);
  
  // Check against profanity list
  for (const word of actualProfanityList) {
    if (normalized.includes(word.toLowerCase())) {
      return true;
    }
  }
  
  // Check against inappropriate patterns
  for (const pattern of inappropriatePatterns) {
    if (pattern.test(text)) {
      return true;
    }
  }
  
  return false;
};

// Check if text is spam-like
const isSpam = (text) => {
  return spamPatterns.some(pattern => pattern.test(text));
};

// Check if name is reserved
const isReserved = (text) => {
  const normalized = normalizeText(text);
  return reservedNames.some(reserved => normalized.includes(reserved));
};

// Main validation function
export const validatePlayerName = (name) => {
  if (!name || typeof name !== 'string') {
    return { isValid: false, reason: 'Name is required' };
  }

  const trimmedName = name.trim();

  // Length checks
  if (trimmedName.length === 0) {
    return { isValid: false, reason: 'Name cannot be empty' };
  }

  if (trimmedName.length > 20) {
    return { isValid: false, reason: 'Name must be 20 characters or less' };
  }

  if (trimmedName.length < 2) {
    return { isValid: false, reason: 'Name must be at least 2 characters' };
  }

  // Content checks
  if (containsProfanity(trimmedName)) {
    return { isValid: false, reason: 'Name contains inappropriate content' };
  }

  if (isSpam(trimmedName)) {
    return { isValid: false, reason: 'Name appears to be spam' };
  }

  if (isReserved(trimmedName)) {
    return { isValid: false, reason: 'Name is not allowed' };
  }

  // Character checks
  if (/^[\s]+$/.test(trimmedName)) {
    return { isValid: false, reason: 'Name cannot be only spaces' };
  }

  // Allow only alphanumeric, spaces, and common symbols
  if (!/^[a-zA-Z0-9\s\-_.'!@#$%^&*()+=<>?/|\\:;"{}[\],~`]+$/.test(trimmedName)) {
    return { isValid: false, reason: 'Name contains invalid characters' };
  }

  return { isValid: true };
};

// Clean/suggest alternative names
export const suggestCleanName = (name) => {
  if (!name) return '';
  
  let cleaned = name.trim();
  
  // Remove excessive repeated characters
  cleaned = cleaned.replace(/(.)\1{3,}/g, '$1$1');
  
  // Limit caps
  if (/^[A-Z]{4,}$/.test(cleaned)) {
    cleaned = cleaned.charAt(0) + cleaned.slice(1).toLowerCase();
  }
  
  // Remove leading/trailing special chars
  cleaned = cleaned.replace(/^[^a-zA-Z0-9]+/, '').replace(/[^a-zA-Z0-9]+$/, '');
  
  // Ensure it's not empty after cleaning
  if (!cleaned) {
    return 'Player';
  }
  
  return cleaned.slice(0, 20);
};

// Additional security: Rate limiting check (client-side)
// Lenient enough for mobile (retries, slow taps) while still limiting abuse
export const checkRateLimit = (gameId) => {
  const key = `leaderboard_attempts_${gameId}`;
  const now = Date.now();
  const windowMs = 5 * 60 * 1000; // 5 minutes
  const maxAttempts = 10;
  
  try {
    const stored = localStorage.getItem(key);
    const attempts = stored ? JSON.parse(stored) : [];
    
    // Filter to only recent attempts
    const recentAttempts = attempts.filter(timestamp => now - timestamp < windowMs);
    
    if (recentAttempts.length >= maxAttempts) {
      const oldestAttempt = Math.min(...recentAttempts);
      const waitTime = Math.ceil((windowMs - (now - oldestAttempt)) / 1000);
      return { 
        allowed: false, 
        waitTime,
        message: `Too many attempts. Please wait ${waitTime} seconds.`
      };
    }
    
    // Add current attempt
    recentAttempts.push(now);
    localStorage.setItem(key, JSON.stringify(recentAttempts));
    
    return { allowed: true };
  } catch (error) {
    // If localStorage fails, allow the attempt
    return { allowed: true };
  }
};

const contentModeration = {
  validatePlayerName,
  suggestCleanName,
  checkRateLimit
};
export default contentModeration; 